import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import AdminLogin from "./components/AdminLogin";
import VillaCard from "./components/VillaCard";
import VillaDetail from "./components/VillaDetail";
import VillaDetailWrapper from "./components/VillaDetailWrapper";
import DestinationDetail from "./components/DestinationDetail";
import SearchPage from "./components/SearchPage";
import InspirationPage from "./components/InspirationPage";
import AboutPage from "./components/AboutPage";
import AdminPage from "./components/AdminPage";
import AdminPageNew from "./components/AdminPageNew";
import LoginPage from "./components/LoginPage";
import FavoritesPage from "./components/FavoritesPage";
import OurDifferencePage from "./components/OurDifferencePage";
import ContactPage from "./components/ContactPage";
import ConciergePage from "./components/ConciergePage";
import PressPage from "./components/PressPage";
import CareersPage from "./components/CareersPage";
import TermsPage from "./components/TermsPage";
import PrivacyPage from "./components/PrivacyPage";
import InsurancePage from "./components/InsurancePage";
import CancellationPage from "./components/CancellationPage";
import ExperiencesPage from "./components/ExperiencesPage";
import EventsPage from "./components/EventsPage";
import LuxuryTransportPage from "./components/LuxuryTransportPage";
import ChefServicePage from "./components/ChefServicePage";
import WellnessPage from "./components/WellnessPage";
import PartnersPage from "./components/PartnersPage";
import HomePageV2 from "./components/HomePageV2";
import EmotionalFilters from "./components/EmotionalFilters";
import ConciergeChat from "./components/ConciergeChat";
import DestinationsSection from "./components/DestinationsSection";
import { Button } from "./components/ui/button";
import { Badge } from "./components/ui/badge";
import { sampleVillas, testimonials, partners } from "./data/mock";
import { LuxurySearch as Search, LuxuryStar as Star, LuxuryChevronRight as ChevronRight, LuxuryMapPin as MapPin, LuxuryAward as Award, LuxuryUsers as Users, MichelinIcon, RelaisIcon, LeadingIcon, VirtuosoIcon } from "./components/icons/LuxuryIcons";
import { useTranslation } from "./hooks/useTranslation";

const HomePage = () => {
  const { t } = useTranslation();
  const [selectedEmotion, setSelectedEmotion] = useState(null);
  const [favorites, setFavorites] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [showConcierge, setShowConcierge] = useState(false);

  // Filter villas based on emotion and search
  const filteredVillas = sampleVillas.filter(villa => {
    const matchesEmotion = !selectedEmotion || villa.moodPrimary === selectedEmotion;
    const matchesSearch = !searchQuery || 
      villa.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      villa.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      villa.emotionalTagline.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesEmotion && matchesSearch;
  });

  const handleToggleFavorite = (villaId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(villaId)) {
        newFavorites.delete(villaId);
      } else {
        newFavorites.add(villaId);
      }
      return newFavorites;
    });
  };

  const handleViewDetails = (villa) => {
    // Navigate to villa detail page
    window.location.href = `/villa/${villa.id}`;
  };

  // Auto-show concierge after some time
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConcierge(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50">
        {/* Hero Section - Full height, starts from top */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          {/* Background Video YouTube */}
          <div className="absolute inset-0 overflow-hidden">
            <iframe
              className="absolute"
              src="https://www.youtube.com/embed/pCt4wPt6zH0?autoplay=1&mute=1&loop=1&playlist=pCt4wPt6zH0&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&disablekb=1&fs=0&cc_load_policy=0&playsinline=1&enablejsapi=0&origin=http://localhost:3004"
              title="PRIVIA Villas - Luxury Background Video"
              frameBorder="0"
              allow="autoplay; encrypted-media"
              allowFullScreen={false}
              style={{
                width: '177.77777778vh', // 16:9 aspect ratio to fill height
                height: '56.25vw', // 16:9 aspect ratio to fill width
                minWidth: '100%',
                minHeight: '100%',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                border: 'none'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
          </div>

          {/* Hero Content */}
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-6">
            <Badge 
              variant="secondary" 
              className="mb-6 px-4 py-2 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
            >
              {t('homepage.heroExperiences')}
            </Badge>
            
            <h1 
              className="text-5xl md:text-7xl font-light mb-6 tracking-wide"
              style={{ 
                fontFamily: 'Playfair Display, serif',
                textShadow: '0 4px 20px rgba(212, 175, 55, 0.3)'
              }}
            >
              {t('homepage.heroTitle')}
            </h1>
            
            <p 
              className="text-xl md:text-2xl font-light mb-8 opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {t('homepage.heroSubtitle')}
            </p>
            
            <p className="text-lg mb-12 opacity-80 italic">
              {t('homepage.heroDescription')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
                onClick={() => document.getElementById('villas-section').scrollIntoView({ behavior: 'smooth' })}
              >
                {t('homepage.discoverVillas')}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
              
              <Button 
                variant="outline"
                size="lg" 
                className="border-2 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
                onClick={() => setShowConcierge(true)}
              >
                {t('homepage.speakToConcierge')}
              </Button>
            </div>
          </div>

          {/* Floating Animation Elements */}
          <div className="absolute top-20 left-20 w-2 h-2 bg-amber-400 rounded-full animate-pulse opacity-60" />
          <div className="absolute bottom-32 right-16 w-3 h-3 bg-blue-300 rounded-full animate-bounce opacity-40" />
          <div className="absolute top-1/3 right-20 w-1 h-1 bg-white rounded-full animate-ping opacity-70" />
        </section>

        {/* Destinations Section */}
        <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50">
          <DestinationsSection />
        </div>

        {/* Search & Filters Section */}
        <section className="py-16 px-6 bg-white/50 backdrop-blur-sm border-y border-gray-100">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-light mb-4 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {t('homepage.findEmotion')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('homepage.findEmotionSubtitle')}
              </p>
            </div>

            {/* Search Bar */}
            <div className="relative max-w-md mx-auto mb-8">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('homepage.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white/80 backdrop-blur-sm"
                style={{ fontFamily: 'Inter, sans-serif' }}
              />
            </div>

            {/* Emotional Filters */}
            <EmotionalFilters 
              selectedEmotion={selectedEmotion}
              onEmotionChange={setSelectedEmotion}
            />
          </div>
        </section>

        {/* Villas Showcase */}
        <section id="villas-section" className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-4xl md:text-5xl font-light mb-6 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {t('homepage.villasTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('homepage.villasSubtitle')}
              </p>
            </div>

            {/* Results Summary */}
            {selectedEmotion && (
              <div className="text-center mb-8">
                <Badge variant="outline" className="px-4 py-2 text-base">
                  {filteredVillas.length} {t('homepage.villasForEmotion')}
                </Badge>
              </div>
            )}

            {/* Villa Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredVillas.map((villa) => (
                <VillaCard
                  key={villa.id}
                  villa={villa}
                  onViewDetails={handleViewDetails}
                  onToggleFavorite={handleToggleFavorite}
                  isFavorite={favorites.has(villa.id)}
                />
              ))}
            </div>

            {filteredVillas.length === 0 && (
              <div className="text-center py-16">
                <p className="text-xl text-gray-500 mb-4">{t('homepage.noVillasFound')}</p>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setSelectedEmotion(null);
                    setSearchQuery("");
                  }}
                >
                  {t('homepage.resetFilters')}
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Social Proof Section */}
        <section className="py-20 px-6 bg-gradient-to-r from-gray-50 to-blue-50">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 
                className="text-3xl md:text-4xl font-light mb-6 text-gray-800"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                {t('homepage.excellenceTitle')}
              </h2>
            </div>

            {/* Testimonials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.id}
                  className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-500" filled={true} />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-4">
                    <img 
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-semibold text-gray-800">{testimonial.name}</p>
                      <p className="text-sm text-gray-600">{testimonial.title}</p>
                      <p className="text-xs text-amber-600">{testimonial.villa}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Partners */}
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-8 uppercase tracking-wider font-light">{t('homepage.prestigePartners')}</p>
              <div className="flex justify-center items-center gap-12 flex-wrap">
                {partners.map((partner, index) => {
                  const iconComponents = {
                    MichelinIcon,
                    RelaisIcon,
                    LeadingIcon,
                    VirtuosoIcon
                  };
                  const IconComponent = iconComponents[partner.icon];
                  
                  return (
                    <div key={index} className="group flex flex-col items-center gap-3 p-4 rounded-lg hover:bg-gray-50/50 transition-all duration-300">
                      <div className="w-8 h-8 text-gray-400 group-hover:text-amber-600 transition-colors duration-300">
                        <IconComponent className="w-full h-full" />
                      </div>
                      <span className="text-xs text-gray-600 font-light tracking-wide uppercase group-hover:text-gray-800 transition-colors duration-300">
                        {partner.name}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 px-6 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-light mb-2">200+</div>
                <p className="text-amber-100">{t('homepage.stats.villas')}</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-light mb-2">35</div>
                <p className="text-amber-100">{t('homepage.stats.destinations')}</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-light mb-2">98%</div>
                <p className="text-amber-100">{t('homepage.stats.satisfaction')}</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-light mb-2">24/7</div>
                <p className="text-amber-100">{t('homepage.stats.service')}</p>
              </div>
            </div>
          </div>
        </section>

      {/* Concierge Chat */}
      {showConcierge && (
        <ConciergeChat 
          triggerMessage="general_welcome"
          onClose={() => setShowConcierge(false)}
        />
      )}
    </div>
    </>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Layout><HomePage /></Layout>} />
              <Route path="/home-v2" element={<Layout><HomePageV2 /></Layout>} />
              <Route path="/villa/:villaId" element={<VillaDetailWrapper />} />
              <Route path="/destination/:destinationId" element={<Layout showNavigation={false} showFooter={false}><DestinationDetail /></Layout>} />
              <Route path="/search" element={<Layout><SearchPage /></Layout>} />
              <Route path="/inspirations" element={<Layout><InspirationPage /></Layout>} />
              <Route path="/about" element={<Layout><AboutPage /></Layout>} />
              
              {/* Route de connexion admin */}
              <Route path="/login" element={<AdminLogin />} />
              
              {/* Route protégée admin */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <AdminPageNew />
                </ProtectedRoute>
              } />
              
              <Route path="/favorites" element={<Layout><FavoritesPage /></Layout>} />
              <Route path="/our-difference" element={<Layout><OurDifferencePage /></Layout>} />
              <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
              <Route path="/conciergerie" element={<Layout><ConciergePage /></Layout>} />
              <Route path="/press" element={<Layout><PressPage /></Layout>} />
              <Route path="/careers" element={<Layout><CareersPage /></Layout>} />
              <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
              <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
              <Route path="/insurance" element={<Layout><InsurancePage /></Layout>} />
              <Route path="/cancellation" element={<Layout><CancellationPage /></Layout>} />
              <Route path="/experiences" element={<Layout><ExperiencesPage /></Layout>} />
              <Route path="/events" element={<Layout><EventsPage /></Layout>} />
              <Route path="/luxury-transport" element={<Layout><LuxuryTransportPage /></Layout>} />
              <Route path="/chef-service" element={<Layout><ChefServicePage /></Layout>} />
              <Route path="/wellness" element={<Layout><WellnessPage /></Layout>} />
              <Route path="/partners" element={<Layout><PartnersPage /></Layout>} />
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </div>
  );
}

export default App;